import { GROUP_COLORS, POUCH_COLORS } from '../types';
import { GROUP_COLOR_HOTKEY_DISPLAY_ORDER } from './groupColorHotkeys';
import { POUCH_COLOR_HOTKEY_DISPLAY_ORDER } from './pouchColorHotkeys';

const MODIFIER_PATTERN =
  /\b(Double Click|Long Press|Click|Shift|Ctrl|Alt|Option|Hold|Space|Delete|Del|Backspace|Drag|Enter|F2|Esc)\b|([⌘⌥⌃⇧])/g;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const SYMBOL_MAP: Record<string, string> = {
  Option: '⌥',
};

const ACTION_WORDS = new Set(['Click', 'Double Click', 'Hold', 'Long Press', 'Drag']);

/**
 * 「W / O / …」や「C / ⇧+C」など、スラッシュ区切りのキー列を <kbd> で囲む。
 * MODIFIER 処理の後に実行（⇧ が先に <kbd> 化された後の「+C」も扱う）
 */
function wrapSlashSeparatedKeys(escaped: string): string {
  return escaped
    .split(' / ')
    .map((segment) => {
      const t = segment.trim();
      if (/^[A-Za-z]$/.test(t)) {
        return `<kbd>${t}</kbd>`;
      }
      const comboPlain = t.match(/^(⇧|Shift)\+([A-Za-z])$/);
      if (comboPlain) {
        return `<kbd>${comboPlain[1]}</kbd>+<kbd>${comboPlain[2]}</kbd>`;
      }
      const comboAfterKbd = t.match(/^<kbd>(⇧|Shift)<\/kbd>\+([A-Za-z])$/);
      if (comboAfterKbd) {
        return `<kbd>${comboAfterKbd[1]}</kbd>+<kbd>${comboAfterKbd[2]}</kbd>`;
      }
      return segment;
    })
    .join(' / ');
}

function wrapKbd(escaped: string): string {
  return wrapSlashSeparatedKeys(
    escaped
      .replace(MODIFIER_PATTERN, (_, word, symbol) => {
        if (word && ACTION_WORDS.has(word)) {
          return `<kbd class="tt-action">${word}</kbd>`;
        }
        return `<kbd>${SYMBOL_MAP[word] || word || symbol}</kbd>`;
      })
      .replace(/<\/kbd>[+ ]<kbd/g, '</kbd><kbd'),
  );
}

function isMacPlatform(): boolean {
  return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/** 操作ガイド用: Mac では Backspace キーを ⌫ と表記 */
function formatGuideBackspaceKeyHtml(): string {
  if (isMacPlatform()) {
    return '<kbd>⌫</kbd>';
  }
  return formatTooltip('Backspace');
}

/** PC 操作ガイド: パウチ — Edit 系（色系・Delete 系の前に並べる）。帳尻パウチは値編集不可のため Enter 行を省略可 */
export function formatPouchGuideEditPcExtrasHtml(opts?: { omitEnter?: boolean }): string {
  if (opts?.omitEnter) {
    return `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">Edit Pouch Title</span>`;
  }
  return [
    `${formatTooltip('Enter')}/<kbd>0</kbd>~<kbd>9</kbd>/<kbd>-</kbd> <span class="tt-desc">Edit Pouch Value</span>`,
    `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">Edit Pouch Title</span>`,
  ].join('<br/>');
}

/** PC 操作ガイド: パウチ — Delete 系 */
export function formatPouchGuideDeletePcExtrasHtml(): string {
  return `${formatTooltip('Del')} / ${formatGuideBackspaceKeyHtml()} <span class="tt-desc">Delete Pouch</span>`;
}

/** PC 操作ガイド: パウチホバー 2 秒後（Edit → Delete ※色は別ブロックで表示） */
export function formatPouchGuidePcExtrasHtml(): string {
  return `${formatPouchGuideEditPcExtrasHtml()}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
}

/** PC: パウチホバー時の矢印（kbd、値行・マルチ行をそれぞれ横並び。Long Press 行は含めない） */
export function formatPouchGuideArrowValueMultiplierPcHtml(): string {
  const kd = (s: string) => `<kbd>${s}</kbd>`;
  const rowVal = `${kd('↑')} <span class="tt-desc">Value +</span>\u3000${kd('↓')} <span class="tt-desc">Value −</span>`;
  const rowMul = `${kd('←')} <span class="tt-desc">Multi −</span>\u3000${kd('→')} <span class="tt-desc">Multi +</span>`;
  return `${rowVal}<br/>${rowMul}`;
}

/** Enter / 0~9 / - — Create Pouch（グループ・計算エリアのガイド共通。Edit 行と表記を揃える） */
export function formatEnterAndDigitsCreatePouchHtml(): string {
  return `${formatTooltip('Enter')}/<kbd>0</kbd>~<kbd>9</kbd>/<kbd>-</kbd> <span class="tt-desc">Create Pouch</span>`;
}

/** F2 / Space — Edit Sheet Title（計算エリアなど） */
export function formatSheetTitleGuideShortcutHtml(): string {
  return `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">Edit Sheet Title</span>`;
}

/**
 * PC: テーブル選択中のパウチ色ガイド（2 秒ホバー追記）。
 * 計算エリアの PouchComponent.pouchGuideColorExtrasHtml と同一の表記・順序（C → Shift+C → 直接キー）。
 * @param currentPouchColorId 代表色（選択パウチ先頭の色など）。未使用時は POUCH_COLORS[0] 相当で解決。
 */
export function formatTableGuidePouchColorKeysPcHtml(shiftKeyDisplay: string, currentPouchColorId: string): string {
  const rawIndex = POUCH_COLORS.findIndex(c => c.id === currentPouchColorId);
  const currentIndex = rawIndex >= 0 ? rawIndex : 0;
  const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
  const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
  const colorCircle = (idx: number) => {
    const color = POUCH_COLORS[idx];
    return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
  };
  const nextCircle = colorCircle(nextIndex);
  const prevCircle = colorCircle(prevIndex);
  const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
    const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
    const circ = idx >= 0 ? colorCircle(idx) : '';
    return `<kbd>${letter}</kbd>${circ}`;
  }).join(' ')}</span>`;
  const rotateNext = `${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${nextCircle}</span>`;
  const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${prevCircle}</span>`;
  return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
}

/**
 * PC: テーブル選択中のグループ色ガイド（2 秒ホバー追記）。
 * 計算エリアの GroupComponent.groupGuideColorExtrasHtml と同一の表記・順序（C → Shift+C → 直接キー）。
 * @param options.tableNoMute テーブル専用: M 省略・ローテは固体色のみ（テーブルは isMuteGroup でミュート行を出さない）
 */
export function formatTableGuideGroupColorKeysPcHtml(
  shiftKeyDisplay: string,
  groupColorId: string,
  options?: { tableNoMute?: boolean },
): string {
  const MUTE_COLOR = '#666666';
  const sym = (color: string, ch: string) =>
    `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
  const colorCircle = (idx: number) => {
    const c = GROUP_COLORS[idx];
    return `<span style="color:${c.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">■</span>`;
  };

  if (options?.tableNoMute) {
    const n = GROUP_COLORS.length;
    const solidLabel = (gi: number) =>
      `Change Group Color to ${sym(GROUP_COLORS[gi].value, '■')}`;
    const solidIndex = (() => {
      const i = GROUP_COLORS.findIndex(c => c.id === groupColorId);
      return i >= 0 ? i : 0;
    })();
    const nextIndex = (solidIndex + 1) % n;
    const prevIndex = (solidIndex - 1 + n) % n;
    const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${solidLabel(nextIndex)}</span>`;
    const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">${solidLabel(prevIndex)}</span>`;
    const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.filter(({ colorId }) => colorId !== 'mute')
      .map(({ letter, colorId }) => {
        const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
        const circ = idx >= 0 ? colorCircle(idx) : '';
        return `<kbd>${letter}</kbd>${circ}`;
      })
      .join(' ')}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
  }

  const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
  const currentColor = groupColorId || GROUP_COLORS[0].id;
  const currentIndex = allColors.findIndex(c => c.id === currentColor);
  const resolvedIndex = currentIndex >= 0 ? currentIndex : 0;
  const nextIndex = (resolvedIndex + 1) % allColors.length;
  const prevIndex = (resolvedIndex - 1 + allColors.length) % allColors.length;
  const colorLabel = (idx: number) =>
    allColors[idx].id === 'mute'
      ? `Enable Mute ${sym(MUTE_COLOR, '▩')}`
      : `Change Group Color to ${sym(allColors[idx].value, '■')}`;
  const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${colorLabel(nextIndex)}</span>`;
  const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">${colorLabel(prevIndex)}</span>`;
  const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
    if (colorId === 'mute') {
      return `<kbd>${letter}</kbd><span style="color:${MUTE_COLOR};font-size:1.3em;line-height:1;vertical-align:-0.05em">▩</span>`;
    }
    const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
    const circ = idx >= 0 ? colorCircle(idx) : '';
    return `<kbd>${letter}</kbd>${circ}`;
  }).join(' ')}</span>`;
  return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
}

/** PC 操作ガイド: グループ — Edit 系 */
export function formatGroupGuideEditPcExtrasHtml(): string {
  return [
    formatEnterAndDigitsCreatePouchHtml(),
    `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">Edit Group Title</span>`,
  ].join('<br/>');
}

/** PC 操作ガイド: グループ — Delete 系 */
export function formatGroupGuideDeletePcExtrasHtml(): string {
  return `${formatTooltip('Del')} / ${formatGuideBackspaceKeyHtml()} <span class="tt-desc">Delete Group</span>`;
}

/** PC 操作ガイド: グループ／リサイズ 2 秒後のキーボード行（Edit → Delete ※色は別ブロック） */
export function formatGroupGuidePcExtrasHtml(): string {
  return `${formatGroupGuideEditPcExtrasHtml()}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
}

export function formatTooltip(text: string): string {
  if (!text) return '';

  const guideEnabled = (window as any).__isGuideEnabled !== false;

  const lines = text.split('\n').map(line => {
    const colonIdx = line.indexOf(': ');
    if (colonIdx === -1) {
      return wrapKbd(escapeHtml(line));
    }

    if (!guideEnabled) return null;

    const keyPart = line.slice(0, colonIdx);
    const descPart = line.slice(colonIdx + 2);

    return `${wrapKbd(escapeHtml(keyPart))} <span class="tt-desc">${escapeHtml(descPart)}</span>`;
  }).filter(Boolean);

  return lines.join('<br/>');
}
